import { useState } from 'react';
import { useApp } from '../../context/AppContext';
import { useSocket } from '../../context/SocketContext';
import {
    Hash,
    Lock,
    ClipboardList,
    Inbox,
    Search,
    Info,
    Users,
    Menu,
    Layers,
    UserPlus,
    Trash2,
    Settings,
    Archive,
    RotateCcw
} from 'lucide-react';
import { api } from '../../api/client';
import { getAvatarColor } from '../../utils/avatar';
import ConfirmModal from '../Modals/ConfirmModal';
import MessageList from './MessageList';
import MessageComposer from './MessageComposer';
import ScheduledMessagesPanel from './ScheduledMessagesPanel';
import OfflineBanner from './OfflineBanner';
import InviteUserModal from '../Modals/InviteUserModal';
import EncryptionSettingsModal from '../Modals/EncryptionSettingsModal';
import TicketDetail from '../Tickets/TicketDetail';
import TicketList from '../Tickets/TicketList';
import GlobalSearchModal from '../Modals/GlobalSearchModal';
import { Channel, Topic, Ticket as TicketType } from '../../types';
import { useMobileMenu } from '../../context/MobileMenuContext';

export default function ChatView() {
    const { activeView, getActiveViewData, currentUser, directMessages, users, settings, hasPermission, messages, archiveTopic, unarchiveTopic, deleteTopic, archiveChannel, unarchiveChannel, deleteChannel } = useApp();
    const { onlineUsers } = useSocket();
    const [showSearch, setShowSearch] = useState(false);
    const [showInfo, setShowInfo] = useState(false);
    const [showSettingsMenu, setShowSettingsMenu] = useState(false);
    const [showInviteModal, setShowInviteModal] = useState(false);
    const [showEncryptionSettings, setShowEncryptionSettings] = useState(false);
    const [confirmRemove, setConfirmRemove] = useState<{ userId: string; userName: string } | null>(null);
    const mobileMenu = useMobileMenu();

    const viewData = getActiveViewData();

    const handleRemoveUser = async () => {
        if (!confirmRemove || !viewData) return;

        try {
            if (activeView.type === 'channel') {
                await api.removeChannelMember(activeView.id, confirmRemove.userId);
            } else if (activeView.type === 'topic') {
                await api.removeTopicMember(activeView.id, confirmRemove.userId);
            }
        } catch (error) {
            console.error('Failed to remove user:', error);
            alert('Failed to remove user');
        }
    };

    const handleArchive = async () => {
        if (!viewData) return;
        try {
            if (activeView.type === 'channel') {
                await archiveChannel(activeView.id);
            } else if (activeView.type === 'topic') {
                await archiveTopic(activeView.id);
            }
            setShowSettingsMenu(false);
        } catch (error) {
            console.error('Failed to archive:', error);
            alert('Failed to archive');
        }
    };

    const handleUnarchive = async () => {
        if (!viewData) return;
        try {
            if (activeView.type === 'channel') {
                await unarchiveChannel(activeView.id);
            } else if (activeView.type === 'topic') {
                await unarchiveTopic(activeView.id);
            }
            setShowSettingsMenu(false);
        } catch (error) {
            console.error('Failed to unarchive:', error);
            alert('Failed to unarchive');
        }
    };

    const handleDelete = async () => {
        if (!viewData) return;
        try {
            if (activeView.type === 'channel') {
                await deleteChannel(activeView.id);
            } else if (activeView.type === 'topic') {
                await deleteTopic(activeView.id);
            }
            setShowSettingsMenu(false);
        } catch (error: any) {
            console.error('Failed to delete:', error);
            alert(error.response?.data?.error || 'Failed to delete');
        }
    };

    const getViewIcon = () => {
        const props = { size: 20, strokeWidth: 1.5, style: { marginRight: 8 } };

        // Render a green padlock if forced E2EE
        let isEncrypted = false;
        if (activeView.type === 'channel') {
            isEncrypted = (viewData as Channel)?.encryption_mode === 'forced';
        } else if (activeView.type === 'dm') {
            isEncrypted = settings?.encryption_policy?.dm_mode === 'forced';
        } else if (activeView.type === 'topic') {
            isEncrypted = (viewData as Topic)?.encryption_mode === 'forced';
        }

        if (isEncrypted) {
            return <Lock {...props} color="var(--accent-primary)" />;
        }

        switch (activeView.type) {
            case 'channel':
                return (viewData as Channel)?.is_private ? <Lock {...props} /> : <Hash {...props} />;
            case 'dm': return <Users {...props} />;
            case 'ticket': return <ClipboardList {...props} />;
            case 'topic': return <Layers {...props} />;
            case 'inbox': return <Inbox {...props} />;
            case 'tickets-list': return <ClipboardList {...props} />;
            default: return <Hash {...props} />;
        }
    };

    const getViewName = () => {
        if (activeView.type === 'dm') {
            const dm = directMessages.find(d => d.id === activeView.id);
            const otherParticipants = dm?.participants?.filter(p => p.id !== currentUser?.id) || [];
            return otherParticipants.map(p => p.name).join(', ') || 'Direct Message';
        }
        if (activeView.type === 'tickets-list') {
            return 'All Tickets';
        }
        return (viewData as any)?.name || (viewData as any)?.title || 'Chat';
    };

    const getViewMeta = () => {
        switch (activeView.type) {
            case 'channel':
                const channel = viewData as Channel;
                return channel?.member_ids ? `${channel.member_ids.length} members` : '';
            case 'dm':
                return 'Direct message';
            case 'ticket':
                const ticket = viewData as TicketType;
                return ticket?.status ? ticket.status.replace('_', ' ') : '';
            case 'topic':
                return 'Topic';
            case 'inbox':
                return 'All your messages';
            case 'tickets-list':
                return 'Manage your tickets';
            default:
                return '';
        }
    };

    // Render ticket list view
    if (activeView.type === 'tickets-list') {
        return (
            <div className="chat-area">
                <header className="chat-header">
                    <div className="chat-header-info">
                        <h1 className="chat-header-name">
                            {getViewIcon()}
                            {getViewName()}
                        </h1>
                        <span className="chat-header-meta">{getViewMeta()}</span>
                    </div>
                </header>
                <TicketList />
            </div>
        );
    }

    // Render ticket detail view
    if (activeView.type === 'ticket') {
        return <TicketDetail ticket={viewData} />;
    }

    return (
        <div className="chat-area">
            <OfflineBanner />
            {/* Header */}
            <header className="chat-header">
                <button className="mobile-menu-btn" onClick={() => mobileMenu?.toggle()}>
                    <Menu size={22} />
                </button>
                <div className="chat-header-info">
                    <h1 className="chat-header-name">
                        {getViewIcon()}
                        {getViewName()}
                    </h1>
                    <span className="chat-header-meta">{getViewMeta()}</span>
                </div>
                <div className="chat-header-actions" style={{ position: 'relative' }}>
                    <>
                        {(activeView.type === 'channel' || activeView.type === 'topic') && (
                            <button
                                className="btn btn-ghost btn-icon"
                                title="Invite Users"
                                onClick={() => setShowInviteModal(true)}
                            >
                                <UserPlus size={20} strokeWidth={1.5} />
                            </button>
                        )}
                        <button
                            className="btn btn-ghost btn-icon"
                            title="Search"
                            onClick={() => setShowSearch(true)}
                        >
                            <Search size={20} strokeWidth={1.5} />
                        </button>
                    </>

                    {(activeView.type === 'channel' || activeView.type === 'topic') && (
                        <div style={{ position: 'relative' }}>
                            <button
                                className={`btn btn-ghost btn-icon ${showSettingsMenu ? 'active' : ''}`}
                                title="Settings"
                                onClick={() => setShowSettingsMenu(!showSettingsMenu)}
                            >
                                <Settings size={20} strokeWidth={1.5} />
                            </button>
                            {showSettingsMenu && viewData && (
                                <div className="menu-dropdown" style={{ right: 0, top: '100%', marginTop: 8 }}>
                                    {/* Encryption Settings - Creator/Admin only */}
                                    {(() => {
                                        const isCreator = (viewData as any).created_by === currentUser?.id;
                                        // TODO: Check actual admin role permission if available on currentUser.role
                                        // For now assuming creator or if admin capability is implemented
                                        // A simple check: if user has role_id and roles are loaded... but let's just stick to creator for now + basic admin check if possible
                                        const isAdmin = hasPermission('admin:view') || hasPermission('super:admin');

                                        if (isCreator || isAdmin) {
                                            return (
                                                <button
                                                    className="menu-item"
                                                    onClick={() => { setShowEncryptionSettings(true); setShowSettingsMenu(false); }}
                                                >
                                                    <Lock size={16} />
                                                    <span>Encryption</span>
                                                </button>
                                            );
                                        }
                                        return null;
                                    })()}

                                    {(viewData as any).is_archived ? (
                                        <button className="menu-item" onClick={handleUnarchive}>
                                            <RotateCcw size={16} />
                                            <span>Unarchive</span>
                                        </button>
                                    ) : (
                                        <>
                                            {/* Check if we can delete (no other messages) */}
                                            {(() => {
                                                const hasOtherMessages = messages.some(m =>
                                                    (activeView.type === 'channel' ? m.channel_id === activeView.id : m.topic_id === activeView.id) &&
                                                    m.author_id !== (viewData as any).created_by
                                                );

                                                if (hasOtherMessages) {
                                                    return (
                                                        <button className="menu-item" onClick={handleArchive}>
                                                            <Archive size={16} />
                                                            <span>Archive</span>
                                                        </button>
                                                    );
                                                } else {
                                                    return (
                                                        <button className="menu-item danger" onClick={handleDelete}>
                                                            <Trash2 size={16} />
                                                            <span>Delete</span>
                                                        </button>
                                                    );
                                                }
                                            })()}
                                        </>
                                    )}
                                </div>
                            )}
                        </div>
                    )}

                    <div style={{ position: 'relative' }}>
                        <button
                            className={`btn btn-ghost btn-icon ${showInfo ? 'active' : ''}`}
                            title="Info"
                            onClick={() => setShowInfo(!showInfo)}
                        >
                            <Info size={20} strokeWidth={1.5} />
                        </button>
                        {showInfo && (
                            <div className="chat-info-panel">
                                <div className="info-section">
                                    <h4 style={{ margin: '0 0 4px', fontSize: 14 }}>{getViewName()}</h4>
                                    <p style={{ fontSize: 13, color: 'var(--text-secondary)', margin: 0 }}>
                                        {getViewMeta()}
                                    </p>
                                </div>
                                <div className="info-section">
                                    <div className="info-section-title">Details</div>
                                    <div style={{ fontSize: 13, color: 'var(--text-secondary)' }}>Type: {activeView.type}</div>
                                    {(activeView.type === 'channel' || activeView.type === 'topic') && (viewData as any).description && (
                                        <div style={{ fontSize: 13, marginTop: 4, color: 'var(--text-secondary)' }}>{(viewData as any).description}</div>
                                    )}
                                </div>

                                {(activeView.type === 'channel' || activeView.type === 'topic') && (
                                    <div className="info-section">
                                        <div className="info-section-title">
                                            Members ({(viewData as Channel | Topic).member_ids?.length || 0})
                                        </div>
                                        <div className="member-list">
                                            {(viewData as Channel | Topic).member_ids?.map(memberId => {
                                                const member = users.find(u => u.id === memberId);
                                                if (!member) return null;
                                                const isOnline = onlineUsers.has(memberId);
                                                const isCreator = (viewData as any).created_by === currentUser?.id;
                                                const isAdmin = hasPermission('admin:view') || hasPermission('super:admin');
                                                const canRemove = (isCreator || isAdmin) && memberId !== currentUser?.id && memberId !== (viewData as any).created_by;

                                                return (
                                                    <div key={memberId} className="member-item">
                                                        <div className="member-info">
                                                            <div style={{ position: 'relative' }}>
                                                                <div className="avatar avatar-xs" style={{ width: 24, height: 24, fontSize: 10, background: getAvatarColor(memberId) }}>
                                                                    {member.name.charAt(0)}
                                                                </div>
                                                                {isOnline && <div className="status-dot online" style={{ width: 8, height: 8, border: '1.5px solid var(--bg-primary)' }} />}
                                                            </div>
                                                            <span className="member-name">
                                                                {member.name} {memberId === currentUser?.id && '(You)'}
                                                            </span>
                                                        </div>
                                                        {canRemove && (
                                                            <button
                                                                className="btn btn-ghost btn-icon btn-sm"
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    setConfirmRemove({ userId: memberId, userName: member.name });
                                                                }}
                                                                title="Remove user"
                                                                style={{ width: 24, height: 24, color: 'var(--text-muted)' }}
                                                            >
                                                                <Trash2 size={14} />
                                                            </button>
                                                        )}
                                                    </div>
                                                );
                                            })}
                                        </div>

                                        {/* Pending Invites */}
                                        {(viewData as Channel | Topic).pending_invites && (viewData as Channel | Topic).pending_invites!.length > 0 && (
                                            <div style={{ marginTop: 16 }}>
                                                <div className="info-section-title">
                                                    Pending ({(viewData as Channel | Topic).pending_invites!.length})
                                                </div>
                                                <div className="member-list">
                                                    {(viewData as Channel | Topic).pending_invites!.map(inviteId => {
                                                        const user = users.find(u => u.id === inviteId);
                                                        if (!user) return null;
                                                        return (
                                                            <div key={inviteId} className="member-item" style={{ opacity: 0.7 }}>
                                                                <div className="member-info">
                                                                    <div className="avatar avatar-xs" style={{ width: 24, height: 24, fontSize: 10, background: 'var(--bg-tertiary)' }}>
                                                                        {user.name.charAt(0)}
                                                                    </div>
                                                                    <span className="member-name">{user.name}</span>
                                                                </div>
                                                            </div>
                                                        );
                                                    })}
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                </div>
            </header >

            {/* Messages */}
            <MessageList searchQuery="" />

            {/* Composer */}
            {
                activeView.type !== 'inbox' && (activeView.type as string) !== 'tickets-list' && (
                    <>
                        <ScheduledMessagesPanel />
                        <MessageComposer
                            key={`composer:${currentUser?.id}:${activeView.type}:${activeView.id}`}
                            draftKey={`${currentUser?.id}:${activeView.type}:${activeView.id}`}
                            placeholder={`Message ${getViewName()}`}
                        />
                    </>
                )
            }

            {
                (activeView.type === 'channel' || activeView.type === 'topic') && viewData && (
                    <InviteUserModal
                        isOpen={showInviteModal}
                        onClose={() => setShowInviteModal(false)}
                        targetId={activeView.id}
                        targetType={activeView.type as 'channel' | 'topic'}
                        currentMemberIds={(viewData as Channel | Topic).member_ids || []}
                        pendingMemberIds={(viewData as Channel | Topic).pending_invites || []}
                    />
                )
            }
            {
                (activeView.type === 'channel' || activeView.type === 'topic') && viewData && (
                    <EncryptionSettingsModal
                        isOpen={showEncryptionSettings}
                        onClose={() => setShowEncryptionSettings(false)}
                        targetId={activeView.id}
                        targetType={activeView.type as 'channel' | 'topic'}
                        currentMode={(viewData as any).encryption_mode || 'optional'}
                    />
                )
            }
            <GlobalSearchModal
                isOpen={showSearch}
                onClose={() => setShowSearch(false)}
                initialScope={activeView.type !== 'inbox' ? { id: activeView.id, type: activeView.type, name: getViewName() } : undefined}
            />
            <ConfirmModal
                isOpen={!!confirmRemove}
                onClose={() => setConfirmRemove(null)}
                onConfirm={handleRemoveUser}
                title="Remove User"
                message={`Are you sure you want to remove ${confirmRemove?.userName} from this ${activeView.type}?`}
                confirmText="Remove"
                type="danger"
            />
        </div >
    );
}
